import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { kalenderAboAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* Kalender-Abo (05.10.2026): Der Inhaber bindet seinen externen Kalender per iCal-Link an (OptiOffice:
   Kalender → iCal) und gibt ihn einzelnen Nutzern frei. Der Link wird nie wieder angezeigt. */

export const zeitpunktDE = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/* Was der Leser nicht sauber verstanden hat — als kurze Sätze für den Besitzer */
export const berichtSaetze = (b) => {
  const s = [];
  if (!b) return s;
  if (b.unlesbar) s.push(`${b.unlesbar} ${b.unlesbar === 1 ? 'Termin war' : 'Termine waren'} nicht lesbar und ${b.unlesbar === 1 ? 'fehlt' : 'fehlen'}`);
  if (b.gekuerzte_serien) s.push(`${b.gekuerzte_serien} ${b.gekuerzte_serien === 1 ? 'Serie ist' : 'Serien sind'} unvollständig (Regel nicht lesbar oder zu viele Wiederholungen)`);
  if (b.unbekannte_zone) s.push(`${b.unbekannte_zone} ${b.unbekannte_zone === 1 ? 'Termin hat' : 'Termine haben'} eine unbekannte Zeitzone, die Uhrzeit kann abweichen`);
  if (b.abgeschnitten) s.push('Der Kalender ist sehr groß, es wurden nicht alle Termine übernommen');
  return s;
};

export function KalenderAboDialog({ offen, onClose, onGeaendert }) {
  const [daten, setDaten] = useState(null);
  const [url, setUrl] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [details, setDetails] = useState('belegt');
  const [freigabe, setFreigabe] = useState([]);
  const [busy, setBusy] = useState(false);
  const [entfernenFrage, setEntfernenFrage] = useState(false);
  const [linkSichtbar, setLinkSichtbar] = useState(false);

  const uebernehmen = (d) => {
    setDaten(d); setUrl(''); setBezeichnung(d.bezeichnung || ''); setDetails(d.details || 'belegt');
    setFreigabe(d.freigegeben_fuer || []); setEntfernenFrage(false); setLinkSichtbar(false);
  };

  useEffect(() => {
    if (!offen) return;
    setDaten(null);
    kalenderAboAPI.lesen().then(r => uebernehmen(r.data)).catch(e => { toast.error(e?.response?.data?.detail || 'Einstellungen konnten nicht geladen werden'); onClose(); });
  }, [offen]); // eslint-disable-line react-hooks/exhaustive-deps

  const meldung = (abruf, gespeichert = true) => {
    if (!abruf) { toast.success('Gespeichert'); return; }
    if (abruf.laeuft) toast.message(`${gespeichert ? 'Gespeichert. ' : ''}Der Kalender wird gerade abgerufen, der Stand folgt gleich`);
    else if (abruf.ok) toast.success(`Kalender gelesen: ${abruf.anzahl} Termine`);
    else toast.error(`${gespeichert ? 'Gespeichert, aber der' : 'Der'} Kalender war nicht lesbar: ${abruf.fehler}`);
  };

  const speichern = async () => {
    if (!daten?.verbunden && !url.trim()) { toast.error('Bitte den Kalender-Link einfügen'); return; }
    setBusy(true);
    try {
      const r = await kalenderAboAPI.speichern({ url: url.trim() || null, bezeichnung, details, freigegeben_fuer: freigabe });
      uebernehmen(r.data); meldung(r.data.abruf); onGeaendert?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    finally { setBusy(false); }
  };

  const abrufen = async () => {
    setBusy(true);
    try { const r = await kalenderAboAPI.abrufen(); uebernehmen(r.data); meldung(r.data.abruf, false); onGeaendert?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Abruf fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const entfernen = async () => {
    setBusy(true);
    try { await kalenderAboAPI.entfernen(); toast.success('Kalender getrennt, Termine gelöscht'); onGeaendert?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht entfernt'); }
    finally { setBusy(false); }
  };

  const umschalten = (uid) => setFreigabe(f => (f.includes(uid) ? f.filter(x => x !== uid) : [...f, uid]));

  return (
    <Dialog open={offen} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="kalender-abo-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Mein Kalender im CRM</DialogTitle></DialogHeader>
        {!daten ? <div className="h-32 bg-muted animate-pulse rounded-lg" /> : (
          <div className="space-y-4 text-sm">
            <p className="text-xs text-muted-foreground">
              Dein Kalender aus OptiOffice oder einem anderen Programm erscheint hier im Kalender, nur lesend. Sehen können ihn nur du und die Personen, die du unten auswählst.
            </p>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Kalender-Link (iCal)</label>
              <div className="flex gap-2">
                <Input type={linkSichtbar ? 'text' : 'password'} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} name="kalender-link" data-1p-ignore="true" data-lpignore="true" value={url} onChange={e => setUrl(e.target.value)} data-testid="kalender-abo-url"
                  placeholder={daten.verbunden ? `Verbunden mit ${daten.anbieter || 'Kalender'}. Neuen Link einfügen, um zu wechseln` : 'https://… oder webcal://…'} />
                {/* Am Handy sieht man sonst nicht, was beim Einfügen wirklich im Feld gelandet ist */}
                {url && <Button type="button" variant="outline" className="shrink-0 px-3" onClick={() => setLinkSichtbar(v => !v)} data-testid="kalender-abo-url-zeigen">{linkSichtbar ? 'Verbergen' : 'Zeigen'}</Button>}
              </div>
              <p className="text-[11px] text-muted-foreground">In OptiOffice: Kalender öffnen, dann „iCal“, und den Link deines eigenen Kalenders kopieren. Der Link wird verschlüsselt gespeichert und hier nie wieder angezeigt.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name in der Anzeige</label>
              <Input value={bezeichnung} onChange={e => setBezeichnung(e.target.value)} placeholder="z. B. Philip (OptiOffice)" maxLength={60} data-testid="kalender-abo-name" />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Wer darf ihn sehen?</label>
              {daten.team?.length ? (
                <div className="rounded-md border border-border divide-y divide-border max-h-44 overflow-y-auto">
                  {daten.team.map(u => (
                    <label key={u.user_id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40">
                      <input type="checkbox" className="accent-marke" checked={freigabe.includes(u.user_id)} onChange={() => umschalten(u.user_id)} data-testid={`kalender-abo-nutzer-${u.user_id}`} />
                      <span className="flex-1 min-w-0 truncate">{u.name}</span>
                      <span className="text-[11px] text-muted-foreground">{u.rolle}</span>
                    </label>
                  ))}
                </div>
              ) : <p className="text-xs text-muted-foreground">Es gibt noch keine weiteren Nutzer.</p>}
              <p className="text-[11px] text-muted-foreground">Ohne Auswahl siehst nur du selbst den Kalender. Die Freigabe gilt für die Person, nicht für ihre Rolle.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Was sehen die Freigegebenen?</label>
              <div className="flex flex-col gap-1.5">
                {[['belegt', 'Nur „Belegt“ mit Uhrzeit', 'Keine Titel, keine Kundennamen.'], ['titel', 'Titel und Uhrzeit', 'Die Titel deiner Termine sind sichtbar, auch Kundennamen darin.']].map(([k, l, h]) => (
                  <label key={k} className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="kalender-abo-details" className="accent-marke mt-1" checked={details === k} onChange={() => setDetails(k)} data-testid={`kalender-abo-details-${k}`} />
                    <span><span className="font-medium">{l}</span><br /><span className="text-[11px] text-muted-foreground">{h}</span></span>
                  </label>
                ))}
              </div>
            </div>

            {daten.verbunden && (
              <div className={`rounded-md border p-2.5 text-xs ${daten.letzter_fehler || daten.veraltet ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-border bg-muted/30'}`} data-testid="kalender-abo-stand">
                {daten.letzter_fehler
                  ? <span className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" /><span>Letzter Abruf fehlgeschlagen: {daten.letzter_fehler}{daten.letzter_erfolg_am ? `. Angezeigt wird der Stand von ${zeitpunktDE(daten.letzter_erfolg_am)}.` : ''}</span></span>
                  : <span>{daten.anzahl_termine} Termine gelesen, Stand {zeitpunktDE(daten.letzter_erfolg_am) || 'noch kein Abruf'}. Aktualisiert sich alle paar Minuten.</span>}
                {!daten.letzter_fehler && daten.anzahl_termine === 0 && daten.letzter_erfolg_am && (
                  <p className="mt-1 text-amber-700 dark:text-amber-400">Der Kalender ist leer. Stimmt der Link, und ist es der Link deines eigenen Kalenders?</p>
                )}
                {berichtSaetze(daten.bericht).map(s => <p key={s} className="mt-1 text-amber-700 dark:text-amber-400" data-testid="kalender-abo-bericht">{s}.</p>)}
              </div>
            )}

            {daten.verlauf?.length > 0 && (
              <details className="text-[11px] text-muted-foreground">
                <summary className="cursor-pointer">Letzte Änderungen (nur für dich sichtbar)</summary>
                <ul className="mt-1 space-y-0.5">
                  {[...daten.verlauf].reverse().map((v, i) => <li key={`${v.am}-${i}`}>{zeitpunktDE(v.am)}: {v.was}</li>)}
                </ul>
              </details>
            )}

            {entfernenFrage ? (
              <div className="rounded-md border border-red-400 bg-red-50 dark:bg-red-950/30 p-3 space-y-2">
                <p className="text-xs">Kalender trennen? Der Link und alle eingelesenen Termine werden im CRM gelöscht. In OptiOffice ändert sich nichts.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" onClick={entfernen} disabled={busy} data-testid="kalender-abo-entfernen-ja">Ja, trennen</Button>
                  <Button size="sm" variant="outline" onClick={() => setEntfernenFrage(false)}>Abbrechen</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button onClick={speichern} disabled={busy} data-testid="kalender-abo-speichern">{daten.verbunden ? 'Speichern' : 'Verbinden'}</Button>
                {daten.verbunden && <Button variant="outline" onClick={abrufen} disabled={busy} className="gap-1"><RefreshCw className="w-3.5 h-3.5" /> Jetzt abrufen</Button>}
                {daten.verbunden && <Button variant="ghost" onClick={() => setEntfernenFrage(true)} disabled={busy} className="gap-1 ml-auto text-muted-foreground"><Trash2 className="w-3.5 h-3.5" /> Trennen</Button>}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
